import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchCurrentUser } from "../lib/auth";
import { fetchCart, previewCoupon, submitCheckout, type CartResponse } from "../lib/cart";
import { formatCurrency } from "../lib/utils";

function normalizePhone(value: string) {
  return value.replace(/[\s-]+/g, "").trim();
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value.trim()) && !value.includes("..");
}

function isValidKenyanPhone(value: string) {
  return /^(?:\+254|254|0)(?:1\d{8}|7\d{8})$/.test(normalizePhone(value));
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [form, setForm] = useState({
    email: "",
    phone: "",
    name: "",
    deliveryLocation: "",
    deliveryNote: ""
  });

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);

      try {
        const [cartData, currentUser] = await Promise.all([fetchCart(), fetchCurrentUser()]);
        if (!ignore) {
          setCart(cartData);
          if (currentUser.user) {
            setForm((current) => ({
              ...current,
              email: currentUser.user.email || current.email,
              phone: currentUser.user.phone || current.phone,
              name: currentUser.user.firstName || current.name
            }));
          }
        }
      } catch {
        if (!ignore) {
          setError("Unable to load checkout right now.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, []);

  function validateForm() {
    const nextErrors: Record<string, string> = {};

    if (form.name.trim().length < 2) {
      nextErrors.name = "Enter your full name";
    }

    if (!isValidEmail(form.email)) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!isValidKenyanPhone(form.phone)) {
      nextErrors.phone = "Enter a valid Kenyan phone number";
    }

    if (form.deliveryLocation.trim().length < 5) {
      nextErrors.deliveryLocation = "Enter a clear delivery location";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await submitCheckout({
        ...form,
        email: form.email.trim(),
        phone: normalizePhone(form.phone),
        name: form.name.trim(),
        deliveryLocation: form.deliveryLocation.trim(),
        deliveryNote: form.deliveryNote.trim(),
        couponCode: couponCode.trim()
      });
      setSuccess(result.message);
      setCart({
        items: [],
        summary: {
          subtotal: 0,
          discount: 0,
          total: 0,
          quantity: 0,
          coupon: null
        }
      });
      navigate(`/shop/invoice?order=${result.orderId}`);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to submit checkout.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApplyCoupon() {
    if (!couponCode.trim()) {
      setError("Enter a coupon code.");
      return;
    }

    setCouponLoading(true);
    setError(null);
    try {
      const nextCart = await previewCoupon({ couponCode: couponCode.trim() });
      setCart(nextCart);
    } catch (couponError) {
      setError(couponError instanceof Error ? couponError.message : "Unable to apply coupon.");
    } finally {
      setCouponLoading(false);
    }
  }

  function handleRemoveCoupon() {
    setCouponCode("");
    setCart((current) =>
      current
        ? {
            ...current,
            summary: {
              ...current.summary,
              discount: 0,
              total: current.summary.subtotal,
              coupon: null
            }
          }
        : current
    );
  }

  return (
    <section className="mx-auto w-full max-w-[92rem] px-3 py-12 sm:px-4 lg:px-5">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="glass-panel rounded-[28px] p-6">
          <p className="eyebrow">Checkout</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight">Delivery and contact details</h1>
          <p className="mt-4 text-sm leading-7 text-nv-slate">
            Provide the required information below. We will contact you through our official channels to confirm delivery and payment.
          </p>
          {loading ? (
            <p className="mt-6 text-sm text-nv-slate">Loading checkout...</p>
          ) : cart && cart.items.length > 0 ? (
            <form className="mt-8 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Full name</label>
                  <input
                    className="field-control px-4 py-3 text-sm"
                    onChange={(event) => {
                      setForm((current) => ({ ...current, name: event.target.value }));
                      setFieldErrors((current) => ({ ...current, name: "" }));
                    }}
                    required
                    value={form.name}
                  />
                  {fieldErrors.name ? <p className="mt-2 text-xs text-red-600">{fieldErrors.name}</p> : null}
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Phone number</label>
                  <input
                    className="field-control px-4 py-3 text-sm"
                    inputMode="tel"
                    onBlur={(event) => {
                      const normalized = normalizePhone(event.target.value);
                      setForm((current) => ({ ...current, phone: normalized }));
                    }}
                    onChange={(event) => {
                      setForm((current) => ({ ...current, phone: event.target.value }));
                      setFieldErrors((current) => ({ ...current, phone: "" }));
                    }}
                    placeholder="+2547XXXXXXXX or 07XXXXXXXX"
                    required
                    value={form.phone}
                  />
                  {fieldErrors.phone ? <p className="mt-2 text-xs text-red-600">{fieldErrors.phone}</p> : null}
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email address</label>
                <input
                  className="field-control px-4 py-3 text-sm"
                  onChange={(event) => {
                    setForm((current) => ({ ...current, email: event.target.value }));
                    setFieldErrors((current) => ({ ...current, email: "" }));
                  }}
                  required
                  type="email"
                  value={form.email}
                />
                {fieldErrors.email ? <p className="mt-2 text-xs text-red-600">{fieldErrors.email}</p> : null}
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery location</label>
                <textarea
                  className="field-control min-h-[120px] px-4 py-3 text-sm"
                  onChange={(event) => {
                    setForm((current) => ({ ...current, deliveryLocation: event.target.value }));
                    setFieldErrors((current) => ({ ...current, deliveryLocation: "" }));
                  }}
                  required
                  value={form.deliveryLocation}
                />
                {fieldErrors.deliveryLocation ? (
                  <p className="mt-2 text-xs text-red-600">{fieldErrors.deliveryLocation}</p>
                ) : null}
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery note</label>
                <textarea className="field-control min-h-[100px] px-4 py-3 text-sm" onChange={(event) => setForm((current) => ({ ...current, deliveryNote: event.target.value }))} placeholder="Optional instructions, landmark, or preferred contact timing" value={form.deliveryNote} />
              </div>
              {error ? <p className="text-sm text-red-600">{error}</p> : null}
              {success ? <p className="text-sm text-emerald-700">{success}</p> : null}
              <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60" disabled={submitting} type="submit">
                {submitting ? "Submitting order..." : "Place order"}
              </button>
            </form>
          ) : (
            <div className="mt-8 rounded-[24px] border border-dashed border-black/10 p-6">
              <h2 className="text-2xl font-semibold tracking-tight">Your cart is empty</h2>
              <p className="mt-3 text-sm text-nv-slate">Add items before proceeding to checkout.</p>
              <Link className="brand-button mt-5 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/shop">
                Continue shopping
              </Link>
            </div>
          )}
        </div>
        <aside className="glass-panel rounded-[28px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-nv-slate">Order review</p>
          <div className="mt-5 space-y-4">
            <div className="rounded-[24px] border border-black/10 bg-white/70 p-4 shadow-[0_20px_45px_-32px_rgba(15,23,42,0.45)] backdrop-blur dark:bg-white/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold tracking-tight">Coupon</p>
                  <p className="mt-1 text-sm text-nv-slate">Apply a valid code before placing the order.</p>
                </div>
                {cart?.summary.coupon ? (
                  <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                    Applied
                  </span>
                ) : null}
              </div>
              <div className="mt-4 flex flex-col gap-3">
                <input
                  className="field-control px-4 py-3 text-sm"
                  onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  value={couponCode}
                />
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={couponLoading}
                    onClick={() => void handleApplyCoupon()}
                    type="button"
                  >
                    {couponLoading ? "Applying..." : "Apply coupon"}
                  </button>
                  {cart?.summary.coupon ? (
                    <button
                      className="rounded-full border border-red-200 px-5 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-50 dark:border-red-900/70 dark:text-red-300 dark:hover:bg-red-950/30"
                      onClick={handleRemoveCoupon}
                      type="button"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                {cart?.summary.coupon ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-200">
                    <p className="font-semibold">{cart.summary.coupon.code} applied</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.16em] text-emerald-700/80 dark:text-emerald-300/80">
                      Discount saved: {formatCurrency(cart.summary.discount)}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            {cart?.items.map((item) => (
              <div className="flex items-start justify-between gap-3 text-sm" key={item.id}>
                <div className="min-w-0">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-nv-slate">Qty {item.quantity}</p>
                </div>
                <span className="shrink-0 font-semibold">{formatCurrency(item.subtotal)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-black/10 pt-4 text-sm text-nv-slate">
              <span>Items</span>
              <span>{cart?.summary.quantity || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-nv-slate">
              <span>Subtotal</span>
              <span>{formatCurrency(cart?.summary.subtotal || 0)}</span>
            </div>
            {(cart?.summary.discount || 0) > 0 ? (
              <div className="flex items-center justify-between text-sm text-nv-slate">
                <span>Discount</span>
                <span>-{formatCurrency(cart?.summary.discount || 0)}</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between text-base font-semibold">
              <span>Total</span>
              <span>{formatCurrency(cart?.summary.total || 0)}</span>
            </div>
            <div className="rounded-2xl border border-black/10 bg-black/5 p-4 text-sm leading-7 text-nv-slate">
              After your order is received, our team will reach out through official Novatech channels to guide delivery and payment.
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
